import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/data';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于亚洲国产高清影视内容精选平台',
  description:
    '亚洲国产高清影视内容精选平台专注于为华语用户提供优质的亚洲国产高清影视内容推荐与深度评测，涵盖剧集、电影、综艺、动漫、纪录片等多元类别。',
  mainEntity: {
    '@type': 'Organization',
    name: '亚洲国产高清影视内容精选平台',
    description:
      '我们致力于成为亚洲国产高清影视内容领域的权威推荐平台，以专业视角为用户筛选最值得观看的亚洲本土高清影视作品。',
    foundingDate: '2024',
    url: 'https://loforce.cn',
  },
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = fd.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('error');
      setFormError('提交失败，请稍后重试');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9tvnb53pcjqs2fcsje0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(fd.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });

      const text = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || text;

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        setFormData({ name: '', email: '', subject: '', message: '' });
        form.reset();
      } else {
        if (String(serverMsg).includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(String(serverMsg || '提交失败，请稍后重试'));
        }
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 亚洲国产高清影视内容精选平台 | 品牌介绍、口碑与联系方式"
        description="了解亚洲国产高清影视内容精选平台的品牌故事、用户口碑与联系方式。我们致力于为华语用户提供优质的亚洲国产高清影视内容推荐与深度评测。"
        keywords="亚洲国产高清,关于我们,品牌介绍,联系方式,影视内容平台,用户口碑"
        canonicalUrl="https://loforce.cn/about"
        schema={[aboutSchema]}
      />

      <div className="pt-20">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-6">
              关于亚洲国产高清
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-4">
              亚洲国产高清影视内容精选平台创立于2024年，致力于成为亚洲国产高清影视内容领域的权威推荐平台。
              我们以专业视角为用户筛选最值得观看的亚洲本土高清影视作品，涵盖剧集、电影、综艺、动漫、纪录片等多元类别。
            </p>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              我们相信，优质的影视内容值得被更多观众发现。每一篇评测、每一份推荐，都凝聚着编辑团队对影视艺术的热爱与专业判断。
              亚洲国产高清，与您一同发现亚洲影视之美。
            </p>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 rounded-xl p-6">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div>
                    <span className="font-heading font-semibold text-foreground-900 text-sm">{t.name}</span>
                    <span className="text-foreground-500 text-xs ml-2">{t.role}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-600 text-sm">
                有任何问题或建议，欢迎随时与我们取得联系
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-100 rounded-xl p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言（不超过500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">
                  {formData.message.length}/500
                </p>
              </div>

              <div className="antispam-field">
                <label htmlFor="phone_alt">备用电话</label>
                <input
                  type="text"
                  id="phone_alt"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formStatus === 'success' && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  感谢您的留言！我们会在1-2个工作日内回复您。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-white rounded-lg font-label font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                发送留言
              </button>
            </form>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-mail-line"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">contact@loforce.cn</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className="ri-wechat-line"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-500">亚洲国产高清</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
                  <i className="ri-time-line"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">工作时间</h3>
                <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              与我们合作
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-6">
              我们欢迎影视制作方、版权方、品牌方等各类合作伙伴。
              如果您有兴趣在亚洲国产高清平台推广您的优质内容，请通过上方联系表单或邮件与我们沟通。
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full font-label font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}